import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ClipboardCheck,
  Database,
  Download,
  Save,
  Users,
} from "lucide-react";

import { getAllStudents } from "../../services/StudentRecordService";

import {
  calculateStudentAttendanceSummary,
  createEmptyWeeks,
  getAllTimesheets,
  getAttendanceTermDates,
  getClassAttendance,
  saveAttendanceTermDates,
  saveClassAttendance,
} from "../../services/AttendanceRecordService";

import AttendanceRecordBrowser from "./AttendanceRecordBrowser";
import {
  downloadAllCoursesAttendanceExcel,
} from "../../services/AttendanceExcelService";

const CAMPUSES = ["Canberra", "Melbourne"];
const TERMS = ["Term 1", "Term 2", "Term 3", "Term 4"];
const WEEKS = Array.from({ length: 10 }, (_, index) => index + 1);

const DEFAULT_TERM_START_DATES = {
  2026: {
    "Term 1": "2026-01-19",
    "Term 2": "2026-04-20",
    "Term 3": "2026-07-20",
    "Term 4": "2026-10-12",
  },
};

const EXCLUDED_STATUSES = [
  "graduated",
  "cancelled",
  "failed",
  "forwarded to debt collector",
  "withdrawn/discontinued",
  "visa refused",
  "new enrolment",
];


function reminderIsChecked(value) {
  if (
    value &&
    typeof value === "object"
  ) {
    return reminderIsChecked(
      value.checked ??
      value.sent ??
      value.value
    );
  }

  if (value === true) {
    return true;
  }

  return ["Y", "YES", "TRUE", "1"].includes(
    String(value || "")
      .trim()
      .toUpperCase()
  );
}

function normaliseDailyReminders(value = {}) {
  return Object.fromEntries(
    Array.from(
      { length: 5 },
      (_, index) => {
        const dayKey = `day${index + 1}`;

        return [
          dayKey,
          reminderIsChecked(
            value?.[dayKey]
          ),
        ];
      }
    )
  );
}

function getAttendanceDaysPerWeek(course = {}) {
  return String(course.classPerWeek || "")
    .trim()
    .toLowerCase() === "5 days"
    ? 5
    : 3;
}

function parseLocalDate(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value?.toDate === "function") {
    const converted = value.toDate();
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  const text = String(value).trim();
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoMatch) {
    const parsedYear = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    const day = Number(isoMatch[3]);

    const date = new Date(
      parsedYear,
      month - 1,
      day,
      12,
      0,
      0,
      0
    );

    return Number.isNaN(date.getTime()) ? null : date;
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateOnly(date) {
  if (!date) {
    return "";
  }

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function getTermEndDate(termStartDate) {
  const start = parseLocalDate(termStartDate);

  if (!start) {
    return "";
  }

  const end = new Date(start);
  end.setDate(end.getDate() + 69);

  return formatDateOnly(end);
}

function courseRunsInTerm(course, termStartDate) {
  const termStart = parseLocalDate(termStartDate);
  const termEnd = parseLocalDate(
    getTermEndDate(termStartDate)
  );

  if (!termStart || !termEnd) {
    return false;
  }

  const courseStart = parseLocalDate(course?.courseStartDate);
  const courseEnd = parseLocalDate(course?.courseEndDate);

  if (courseStart && courseStart > termEnd) {
    return false;
  }

  if (courseEnd && courseEnd < termStart) {
    return false;
  }

  return true;
}

function normaliseRow(student, savedStudent) {
  const emptyWeeks = createEmptyWeeks();
  const savedWeeks = savedStudent?.weeks || {};

  return {
    ...savedStudent,
    ...student,

    /*
     * Keep reminder information already entered in Records Audit.
     * Without these fields, saving attendance again could overwrite
     * the week and remove its reminder history.
     */
    weeks: Object.fromEntries(
      WEEKS.map((weekNumber) => {
        const key = `week${weekNumber}`;
        const savedWeek = savedWeeks[key] || {};

        return [
          key,
          {
            ...savedWeek,

            attendanceHours:
              savedWeek.attendanceHours === 0
                ? 0
                : savedWeek.attendanceHours ??
                  emptyWeeks[key].attendanceHours,

            leaveApplied: Boolean(
              savedWeek.leaveApplied
            ),

            leaveHours:
              savedWeek.leaveHours === 0
                ? 0
                : savedWeek.leaveHours ??
                  emptyWeeks[key].leaveHours,

            dailyReminders:
              normaliseDailyReminders(
                savedWeek.dailyReminders ??
                savedWeek.dailyAttendanceReminders
              ),

            weeklyReminderSent:
              reminderIsChecked(
                savedWeek.weeklyReminderSent ??
                savedWeek.weeklyAttendanceReminderSent
              ),

            studentResponse: String(
              savedWeek.studentResponse || ""
            ),
          },
        ];
      })
    ),
  };
}


function buildRosterForCourse({
  students,
  campus,
  courseName,
  termStartDate,
}) {
  if (!courseName || !termStartDate) {
    return [];
  }

  const rosterRows = [];

  students.forEach((student) => {
    const form = student?.formState || {};

    const studentCampus = String(
      form.campusName || ""
    ).toLowerCase();

    const status = String(
      form.studentStatus || ""
    ).toLowerCase();

    if (
      !studentCampus.includes(
        String(campus || "").toLowerCase()
      )
    ) {
      return;
    }

    if (
      EXCLUDED_STATUSES.some((item) =>
        status.includes(item)
      )
    ) {
      return;
    }

    const matchingCourse = (
      form.courses || []
    ).find((course) => {
      const cancelled =
        String(
          course.coeStatus || ""
        ).toLowerCase() === "cancelled";

      return (
        course.courseName === courseName &&
        !cancelled &&
        courseRunsInTerm(
          course,
          termStartDate
        )
      );
    });

    const studentId = String(
      form.studentId || ""
    ).trim();

    if (!matchingCourse || !studentId) {
      return;
    }

    const rawEnrolmentType = String(
      form.enrolmentType || ""
    )
      .trim()
      .toUpperCase();

    const enrolmentType =
    rawEnrolmentType === "VOE"
      ? "VOE"
      : "";

    rosterRows.push({
      studentId,

      studentName:
        `${form.firstName || ""} ${
          form.lastName || ""
        }`.trim(),

      enrolmentType,

      contactNumber: String(
        form.phone || ""
      ).trim(),

      contactEmail: String(
        form.email || ""
      ).trim(),

      /*
       * Records Audit uses this value to enable
       * Day 1-Day 3 or Day 1-Day 5 reminder checkboxes.
       */
      attendanceDaysPerWeek:
        getAttendanceDaysPerWeek(matchingCourse),
    });
  });

  return rosterRows.sort((a, b) =>
    a.studentName.localeCompare(
      b.studentName
    )
  );
}

function courseNameKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function findSavedTimesheet(
  timesheets,
  courseCode,
  courseName
) {
  const wantedCode = String(
    courseCode || ""
  )
    .trim()
    .toLowerCase();

  const wantedName = courseNameKey(
    courseName
  );

  return (
    timesheets.find((timesheet) => {
      const savedCode = String(
        timesheet.courseCode || ""
      )
        .trim()
        .toLowerCase();

      return (
        wantedCode &&
        savedCode === wantedCode
      );
    }) ||
    timesheets.find(
      (timesheet) =>
        courseNameKey(
          timesheet.courseName
        ) === wantedName
    ) ||
    null
  );
}

export default function RecordAttendanceTab({
  courseCodeMap = {},
}) {
  const currentYear = new Date().getFullYear();

  const [innerTab, setInnerTab] = useState("enter");
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [campus, setCampus] = useState("Canberra");
  const [year, setYear] = useState(currentYear);
  const [term, setTerm] = useState("Term 1");

  const [termStartDate, setTermStartDate] = useState("");
  const [termEndDate, setTermEndDate] = useState("");
  const [loadingTermDates, setLoadingTermDates] = useState(false);
  const [savingTermDates, setSavingTermDates] = useState(false);

  const [courseName, setCourseName] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [recordStatus, setRecordStatus] = useState("draft");
  const [message, setMessage] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    getAllStudents()
      .then((data) => {
        setStudents(data || []);
      })
      .catch((error) => {
        console.error(error);
        setMessage("Student records could not be loaded.");
      })
      .finally(() => {
        setLoadingStudents(false);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadSavedTermDates() {
      setCourseName("");
      setRows([]);
      setMessage("");
      setLoadingTermDates(true);

      const defaultStartDate =
        DEFAULT_TERM_START_DATES[year]?.[term] || "";

      try {
        const savedTermDates =
          await getAttendanceTermDates({
            campus,
            year: Number(year),
            term,
          });

        if (cancelled) {
          return;
        }

        const nextStartDate =
          savedTermDates?.termStartDate ||
          defaultStartDate ||
          "";

        const nextEndDate =
          savedTermDates?.termEndDate ||
          (
            nextStartDate
              ? getTermEndDate(nextStartDate)
              : ""
          );

        setTermStartDate(nextStartDate);
        setTermEndDate(nextEndDate);
      } catch (error) {
        console.error(
          "Term date loading error:",
          error
        );

        if (!cancelled) {
          setTermStartDate(defaultStartDate);

          setTermEndDate(
            defaultStartDate
              ? getTermEndDate(defaultStartDate)
              : ""
          );

          setMessage(
            "Saved term dates could not be loaded. Default or blank dates are shown."
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingTermDates(false);
        }
      }
    }

    loadSavedTermDates();

    return () => {
      cancelled = true;
    };
  }, [campus, year, term]);

  const courseNames = useMemo(() => {
    if (!termStartDate) {
      return [];
    }

    const names = new Set();

    students.forEach((student) => {
      const form = student?.formState || {};

      const studentCampus = String(
        form.campusName || ""
      ).toLowerCase();

      const status = String(
        form.studentStatus || ""
      ).toLowerCase();

      if (
        !studentCampus.includes(
          campus.toLowerCase()
        )
      ) {
        return;
      }

      if (
        EXCLUDED_STATUSES.some((item) =>
          status.includes(item)
        )
      ) {
        return;
      }

      (form.courses || []).forEach((course) => {
        if (
          String(course.coeStatus || "").toLowerCase() ===
          "cancelled"
        ) {
          return;
        }

        if (!courseRunsInTerm(course, termStartDate)) {
          return;
        }

        if (course.courseName) {
          names.add(course.courseName);
        }
      });
    });

    return [...names].sort((a, b) =>
      a.localeCompare(b)
    );
  }, [students, campus, termStartDate]);

  useEffect(() => {
    if (
      courseName &&
      !courseNames.includes(courseName)
    ) {
      setCourseName("");
      setRows([]);
    }
  }, [courseName, courseNames]);

  const roster = useMemo(
    () =>
      buildRosterForCourse({
        students,
        campus,
        courseName,
        termStartDate,
      }),
    [
      students,
      campus,
      courseName,
      termStartDate,
    ]
  );

  const metadata = useMemo(
    () => ({
      campus,
      courseCode: courseCodeMap[courseName] || "",
      courseName,
      year: Number(year),
      term,
      termStartDate,
      termEndDate,
    }),
    [
      campus,
      courseCodeMap,
      courseName,
      year,
      term,
      termStartDate,
      termEndDate,
    ]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadTimesheet() {
      setMessage("");
      setDirty(false);

      if (!courseName || roster.length === 0) {
        setRows(
          roster.map((student) =>
            normaliseRow(student, null)
          )
        );

        setRecordStatus("draft");
        return;
      }

      setLoadingRecord(true);

      try {
        const savedRows = await getClassAttendance({
          metadata,

          studentIds: roster.map(
            (student) => student.studentId
          ),
        });

        if (cancelled) {
          return;
        }

        const savedByStudentId = new Map(
          savedRows.map((student) => [
            String(student.studentId),
            student,
          ])
        );

        setRows(
          roster.map((student) =>
            normaliseRow(
              student,
              savedByStudentId.get(
                String(student.studentId)
              )
            )
          )
        );

        setRecordStatus(
          savedRows[0]?.recordStatus || "draft"
        );
      } catch (error) {
        console.error(error);

        if (!cancelled) {
          setRows(
            roster.map((student) =>
              normaliseRow(student, null)
            )
          );

          setMessage(
            "The saved timesheet could not be loaded. A new sheet is shown."
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingRecord(false);
        }
      }
    }

    loadTimesheet();

    return () => {
      cancelled = true;
    };
  }, [courseName, metadata, roster]);

  const saveTermDateSettings = async () => {
    if (!termStartDate || !termEndDate) {
      setMessage(
        "Enter both the term start date and term end date before saving."
      );

      return;
    }

    const parsedStartDate =
      parseLocalDate(termStartDate);

    const parsedEndDate =
      parseLocalDate(termEndDate);

    if (
      !parsedStartDate ||
      !parsedEndDate
    ) {
      setMessage(
        "Enter valid term start and end dates."
      );

      return;
    }

    if (parsedEndDate < parsedStartDate) {
      setMessage(
        "The term end date cannot be earlier than the term start date."
      );

      return;
    }

    setSavingTermDates(true);
    setMessage("");

    try {
      await saveAttendanceTermDates({
        campus,
        year: Number(year),
        term,
        termStartDate,
        termEndDate,
      });

      setMessage(
        `Term dates saved successfully for ${campus}, ${year}, ${term}.`
      );
    } catch (error) {
      console.error(
        "Term date saving error:",
        error
      );

      setMessage(
        "The term dates could not be saved. Please try again."
      );
    } finally {
      setSavingTermDates(false);
    }
  };

  const updateWeek = (
    rowIndex,
    weekNumber,
    field,
    value
  ) => {
    const key = `week${weekNumber}`;

    setDirty(true);

    setRows((currentRows) =>
      currentRows.map((row, index) => {
        if (index !== rowIndex) {
          return row;
        }

        const nextWeek = {
          ...row.weeks[key],
          [field]: value,
        };

        if (
          field === "leaveApplied" &&
          !value
        ) {
          nextWeek.leaveHours = "";
        }

        return {
          ...row,

          weeks: {
            ...row.weeks,
            [key]: nextWeek,
          },
        };
      })
    );
  };

  const saveTimesheet = async (nextStatus) => {
    if (
      !courseName ||
      !termStartDate ||
      rows.length === 0
    ) {
      setMessage(
        "Select a term start date and course with students before saving."
      );

      return;
    }

    setSaving(true);
    setMessage("");

    try {
      await saveClassAttendance({
        metadata: {
          ...metadata,
          recordStatus: nextStatus,
        },

        students: rows,
      });

      setRecordStatus(nextStatus);
      setDirty(false);

      setMessage(
        nextStatus === "submitted"
          ? "Timesheet saved or updated successfully."
          : "Timesheet draft saved or updated successfully."
      );
    } catch (error) {
      console.error(error);

      setMessage(
        "The timesheet could not be saved. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const downloadExcel = async () => {
    if (!termStartDate) {
      setMessage(
        "Select a valid term start date before downloading."
      );

      return;
    }

    setDownloadingExcel(true);
    setMessage("");

    try {
      const savedTimesheets =
        await getAllTimesheets({
          year: Number(year),
          term,
          campus,
        });

      /*
       * Contact details remain in the student record,
       * rather than being duplicated in attendanceTimesheets.
       */
      const currentStudentDetailsById =
        new Map(
          students
            .map((student) => {
              const form =
                student?.formState || {};

              const studentId = String(
                form.studentId || ""
              ).trim();

              if (!studentId) {
                return null;
              }

              const rawEnrolmentType =
                String(
                  form.enrolmentType || ""
                )
                  .trim()
                  .toUpperCase();
              const matchingCourse = (
                form.courses || []
              ).find((course) => {
                const cancelled =
                  String(
                    course.coeStatus || ""
                  ).toLowerCase() === "cancelled";

                return (
                  course.courseName === courseName &&
                  !cancelled &&
                  courseRunsInTerm(
                    course,
                    termStartDate
                  )
                );
              });

              return [
                studentId,
                {
                  enrolmentType:
                    rawEnrolmentType === "VOE"
                      ? "VOE"
                      : "",

                  contactNumber: String(
                    form.phone || ""
                  ).trim(),

                  contactEmail: String(
                    form.email || ""
                  ).trim(),

                  attendanceDaysPerWeek:
                    getAttendanceDaysPerWeek(
                      matchingCourse
                    ),
                },
              ];
            })
            .filter(Boolean)
        );

      const coursesForExport =
        courseNames
          .map((targetCourseName) => {
            const targetCourseCode =
              courseCodeMap[
                targetCourseName
              ] || "";

            const savedTimesheet =
              findSavedTimesheet(
                savedTimesheets,
                targetCourseCode,
                targetCourseName
              );

            const courseRoster =
              buildRosterForCourse({
                students,
                campus,
                courseName:
                  targetCourseName,
                termStartDate,
              });

            const savedStudentsById =
              new Map(
                Object.values(
                  savedTimesheet?.students ||
                    {}
                ).map((student) => [
                  String(
                    student.studentId || ""
                  ).trim(),
                  student,
                ])
              );

            let courseStudents =
              courseRoster.map((student) =>
                normaliseRow(
                  student,
                  savedStudentsById.get(
                    String(
                      student.studentId
                    ).trim()
                  )
                )
              );

            // Keep unsaved screen edits for the
            // currently selected course.
            if (
              targetCourseName ===
                courseName &&
              rows.length > 0
            ) {
              courseStudents = rows;
            }

            return {
              courseCode:
                targetCourseCode ||
                savedTimesheet
                  ?.courseCode ||
                "",

              courseName:
                targetCourseName,

              students:
                courseStudents,
            };
          })
          .filter(
            (course) =>
              course.students.length > 0
          );

      // Include saved course timesheets that are
      // not currently returned by the active roster.
      const includedCourseNames = new Set(
        coursesForExport.map((course) =>
          courseNameKey(
            course.courseName
          )
        )
      );

      savedTimesheets.forEach(
        (timesheet) => {
          const savedCourseName =
            String(
              timesheet.courseName || ""
            ).trim();

          const savedCourseKey =
            courseNameKey(
              savedCourseName
            );

          const savedStudents =
            Object.values(
              timesheet.students || {}
            ).map((student) => {
              const studentId = String(
                student.studentId || ""
              ).trim();

              const currentDetails =
                currentStudentDetailsById.get(
                  studentId
                ) || {};

              return {
                ...student,

                enrolmentType:
                  String(
                    currentDetails.enrolmentType ||
                    student.enrolmentType ||
                    ""
                  )
                    .trim()
                    .toUpperCase() === "VOE"
                    ? "VOE"
                    : "",

                contactNumber:
                  currentDetails.contactNumber ||
                  student.contactNumber ||
                  "",

                contactEmail:
                  currentDetails.contactEmail ||
                  student.contactEmail ||
                  "",

                attendanceDaysPerWeek:
                  currentDetails.attendanceDaysPerWeek ??
                  student.attendanceDaysPerWeek ??
                  null,
              };
            });

          if (
            !savedCourseName ||
            includedCourseNames.has(
              savedCourseKey
            ) ||
            savedStudents.length === 0
          ) {
            return;
          }

          coursesForExport.push({
            courseCode:
              timesheet.courseCode || "",
            courseName:
              savedCourseName,
            students:
              savedStudents,
          });

          includedCourseNames.add(
            savedCourseKey
          );
        }
      );

      if (coursesForExport.length === 0) {
        throw new Error(
          "No course attendance records were found for the selected campus, year and term."
        );
      }

      await downloadAllCoursesAttendanceExcel({
        campus,
        year: Number(year),
        term,
        termStartDate,
        termEndDate,
        courses: coursesForExport,
      });

      setMessage(
        `Excel downloaded successfully with ${
          coursesForExport.length
        } course sheet${
          coursesForExport.length === 1
            ? ""
            : "s"
        }.`
      );
    } catch (error) {
      console.error(
        "Excel attendance download error:",
        error
      );

      setMessage(
        error?.message ||
          "The Excel attendance file could not be generated. Please try again."
      );
    } finally {
      setDownloadingExcel(false);
    }
  };

  const years = Array.from(
    { length: 7 },
    (_, index) => currentYear - 2 + index
  );

  return (
    <div
      style={{
        padding: "1.25rem 1rem",
        maxWidth: 1900,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 5,
        }}
      >
        <ClipboardCheck
          size={22}
          color="#056ec4"
        />

        <h2
          style={{
            margin: 0,
            color: "#1e293b",
            fontSize: "1.2rem",
          }}
        >
          Record Student Attendance
        </h2>
      </div>

      <p
        style={{
          margin: "0 0 14px",
          color: "#64748b",
          fontSize: "0.84rem",
        }}
      >
        One student row contains Week 1 to Week 10.
        Approved leave overrides attendance for that week when
        calculating credited hours.
      </p>

      <div style={topActionRowStyle}>
        <div style={innerTabBarStyle}>
            <button
            type="button"
            onClick={() => setInnerTab("enter")}
            style={innerTabButtonStyle(
                innerTab === "enter"
            )}
            >
            <ClipboardCheck size={14} />
            Enter Attendance
            </button>

            <button
            type="button"
            onClick={() => setInnerTab("find")}
            style={innerTabButtonStyle(
                innerTab === "find"
            )}
            >
            <Database size={14} />
            Records Audit
            </button>
        </div>

        {innerTab === "enter" && (
            <button
            type="button"
            onClick={downloadExcel}
            disabled={
                downloadingExcel ||
                saving ||
                !termStartDate
            }
            style={{
                ...excelButtonStyle,

                opacity:
                downloadingExcel ||
                saving ||
                !termStartDate
                    ? 0.65
                    : 1,

                cursor:
                downloadingExcel ||
                saving ||
                !termStartDate
                    ? "not-allowed"
                    : "pointer",
            }}
            >
            <Download size={14} />

            {downloadingExcel
                ? "Generating Excel…"
                : "Download All Courses Excel"}
            </button>
        )}
        </div>

      {innerTab === "find" ? (
        <AttendanceRecordBrowser
          studentRecords={students}
        />
      ) : (
        <>
          <div style={panelStyle}>
          {/* First row */}
          <div style={termFilterGridStyle}>
            <Field label="Campus">
              <select
                value={campus}
                onChange={(event) =>
                  setCampus(event.target.value)
                }
                style={fieldStyle}
              >
                {CAMPUSES.map((item) => (
                  <option key={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Year">
              <select
                value={year}
                onChange={(event) =>
                  setYear(Number(event.target.value))
                }
                style={fieldStyle}
              >
                {years.map((item) => (
                  <option key={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Term">
              <select
                value={term}
                onChange={(event) =>
                  setTerm(event.target.value)
                }
                style={fieldStyle}
              >
                {TERMS.map((item) => (
                  <option key={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Term Start Date">
              <input
                type="date"
                value={termStartDate}
                disabled={loadingTermDates}
                onChange={(event) => {
                  const nextStartDate =
                    event.target.value;

                  setTermStartDate(nextStartDate);

                  setTermEndDate(
                    nextStartDate
                      ? getTermEndDate(nextStartDate)
                      : ""
                  );

                  setCourseName("");
                  setRows([]);
                }}
                style={fieldStyle}
              />
            </Field>

            <Field label="Term End Date">
              <input
                type="date"
                value={termEndDate}
                disabled={loadingTermDates}
                onChange={(event) =>
                  setTermEndDate(event.target.value)
                }
                style={fieldStyle}
              />
            </Field>

            <div style={termDateButtonWrapperStyle}>
              <button
                type="button"
                onClick={saveTermDateSettings}
                disabled={
                  loadingTermDates ||
                  savingTermDates ||
                  !termStartDate ||
                  !termEndDate
                }
                style={{
                  ...termDateButtonStyle,

                  opacity:
                    loadingTermDates ||
                    savingTermDates ||
                    !termStartDate ||
                    !termEndDate
                      ? 0.65
                      : 1,

                  cursor:
                    loadingTermDates ||
                    savingTermDates ||
                    !termStartDate ||
                    !termEndDate
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                <Save size={14} />

                {savingTermDates
                  ? "Saving…"
                  : "Save Term Dates"}
              </button>
            </div>
          </div>

          {/* Separate course row */}
          <div style={courseRowStyle}>
            <Field label="Course">
              <select
                value={courseName}
                disabled={
                  loadingTermDates ||
                  !termStartDate ||
                  !termEndDate
                }
                onChange={(event) => {
                  setCourseName(event.target.value);
                  setMessage("");
                }}
                style={courseSelectStyle}
              >
                <option value="">
                  Select course
                </option>

                {courseNames.map((item) => (
                  <option
                    key={item}
                    value={item}
                  >
                    {courseCodeMap[item]
                      ? `${courseCodeMap[item]} - `
                      : ""}
                    {item}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div style={attendanceSummaryStyle}>
            <span style={pillStyle}>
              <Users size={12} />
              {roster.length} students
            </span>

            <span style={pillStyle}>
              {year} · {term}
            </span>

            <span style={pillStyle}>
              {termStartDate || "No start date"}
              {" to "}
              {termEndDate || "No end date"}
            </span>
          </div>
        </div>

          {message && (
            <div style={messageStyle(message)}>
              {message}
            </div>
          )}

          <div
            style={{
              ...panelStyle,
              padding: 0,
              overflow: "hidden",
            }}
          >
            <div style={actionBarStyle}>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    color: "#64748b",
                    fontSize: "0.8rem",
                  }}
                >
                  {dirty
                    ? "Unsaved changes"
                    : "Timesheet is up to date"}
                </span>

                <span style={statusStyle(recordStatus)}>
                  {recordStatus}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 7,
                  flexWrap: "wrap",
                }}
              >

                <button
                  type="button"
                  onClick={() =>
                    saveTimesheet("submitted")
                  }
                  disabled={!rows.length || saving}
                  style={primaryButtonStyle}
                >
                  <CheckCircle2 size={14} />
                  Save / Update Timesheet
                </button>
              </div>
            </div>

            <div
              style={{
                overflowX: "auto",
                maxHeight: "70vh",
              }}
            >
              {loadingStudents || loadingRecord ? (
                <div style={emptyStyle}>
                  Loading attendance timesheet…
                </div>
              ) : rows.length === 0 ? (
                <div style={emptyStyle}>
                  Select a valid term start date and course
                  to load students active during that term.
                </div>
              ) : (
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          ...headerCellStyle,
                          minWidth: 115,
                        }}
                      >
                        Student ID
                      </th>

                      <th
                        style={{
                          ...headerCellStyle,
                          minWidth: 190,
                        }}
                      >
                        Student Name
                      </th>

                      {WEEKS.map((weekNumber) => (
                        <th
                          key={weekNumber}
                          style={{
                            ...headerCellStyle,
                            minWidth: 165,
                            textAlign: "center",
                          }}
                        >
                          Week {weekNumber}
                        </th>
                      ))}

                      <th
                        style={{
                          ...headerCellStyle,
                          minWidth: 120,
                          textAlign: "center",
                        }}
                      >
                        Attendance %
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((row, rowIndex) => {
                      const summary =
                        calculateStudentAttendanceSummary(row);
                      const hoursPerWeek =
                        summary.hoursPerWeek;

                      return (
                        <tr key={row.studentId}>
                          <td style={bodyCellStyle}>
                            <strong>
                              {row.studentId}
                            </strong>
                          </td>

                          <td style={bodyCellStyle}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 7,
                                flexWrap: "wrap",
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: 600,
                                }}
                              >
                                {row.studentName}
                              </span>

                              {row.enrolmentType === "VOE" && (
                                <span
                                  style={enrolmentTypeStyle(
                                    row.enrolmentType
                                  )}
                                >
                                  {row.enrolmentType}
                                </span>
                              )}
                            </div>
                          </td>

                          {WEEKS.map((weekNumber) => {
                            const key = `week${weekNumber}`;
                            const week = row.weeks[key];

                            return (
                              <td
                                key={weekNumber}
                                style={bodyCellStyle}
                              >
                                <div style={weekCellStyle}>
                                  <label style={miniLabelStyle}>
                                    Attendance hours

                                    <input
                                      type="number"
                                      min="0"
                                      max={hoursPerWeek}
                                      value={
                                        week.attendanceHours
                                      }
                                      onChange={(event) =>
                                        updateWeek(
                                          rowIndex,
                                          weekNumber,
                                          "attendanceHours",
                                          event.target.value
                                        )
                                      }
                                      onWheel={(event) => event.currentTarget.blur()}
                                      style={cellInputStyle}
                                    />
                                  </label>

                                  <label style={checkboxRowStyle}>
                                    <input
                                      type="checkbox"
                                      checked={week.leaveApplied}
                                      onChange={(event) =>
                                        updateWeek(
                                          rowIndex,
                                          weekNumber,
                                          "leaveApplied",
                                          event.target.checked
                                        )
                                      }
                                    />
                                    Leave applied
                                  </label>

                                  <label style={miniLabelStyle}>
                                    Leave hours

                                    <input
                                      type="number"
                                      min="0"
                                      max={hoursPerWeek}
                                      value={week.leaveHours}
                                      disabled={!week.leaveApplied}
                                      onWheel={(event) => event.currentTarget.blur()}
                                      placeholder={
                                        week.leaveApplied
                                          ? `Blank = full  ${hoursPerWeek}h`
                                          : ""
                                      }
                                      onChange={(event) =>
                                        updateWeek(
                                          rowIndex,
                                          weekNumber,
                                          "leaveHours",
                                          event.target.value
                                        )
                                      }
                                      style={{
                                        ...cellInputStyle,

                                        background:
                                          week.leaveApplied
                                            ? "#fff"
                                            : "#f1f5f9",
                                      }}
                                    />
                                  </label>
                                </div>
                              </td>
                            );
                          })}

                          <td style={totalCellStyle}>
                            <span
                              style={attendancePercentageStyle(
                                summary.attendancePercentage
                              )}
                            >
                              {summary.attendancePercentage}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={labelStyle}>
        {label}
      </label>

      {children}
    </div>
  );
}

function innerTabButtonStyle(selected) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,

    border: selected
      ? "1px solid #056ec4"
      : "1px solid transparent",

    borderRadius: 6,
    padding: "7px 10px",

    background: selected
      ? "#056ec4"
      : "transparent",

    color: selected
      ? "#fff"
      : "#475569",

    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.78rem",
  };
}

function statusStyle(status) {
  const submitted =
    status === "submitted";

  return {
    padding: "3px 8px",
    borderRadius: 20,

    background: submitted
      ? "#f0fdf4"
      : "#fff7ed",

    color: submitted
      ? "#166534"
      : "#9a3412",

    border: `1px solid ${
      submitted
        ? "#bbf7d0"
        : "#fed7aa"
    }`,

    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "capitalize",
  };
}

function messageStyle(message) {
  const success = message
    .toLowerCase()
    .includes("successfully");

  return {
    marginBottom: 12,
    padding: "10px 12px",
    borderRadius: 8,

    background: success
      ? "#f0fdf4"
      : "#fff7ed",

    color: success
      ? "#166534"
      : "#9a3412",

    border: `1px solid ${
      success
        ? "#bbf7d0"
        : "#fed7aa"
    }`,

    fontSize: "0.82rem",
  };
}

function enrolmentTypeStyle(enrolmentType) {
  const isCOE = enrolmentType === "COE";

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 40,
    padding: "2px 7px",
    borderRadius: 20,
    background: isCOE
      ? "#eff6ff"
      : "#fff7ed",
    color: isCOE
      ? "#1d4ed8"
      : "#c2410c",
    border: `1px solid ${
      isCOE
        ? "#bfdbfe"
        : "#fed7aa"
    }`,
    fontSize: "0.66rem",
    fontWeight: 800,
  };
}

function attendancePercentageStyle(percentage) {
  const high = percentage >= 80;
  const warning =
    percentage >= 70 &&
    percentage < 80;

  return {
    display: "inline-block",
    minWidth: 58,
    padding: "4px 9px",
    borderRadius: 20,
    fontWeight: 700,

    background: high
      ? "#f0fdf4"
      : warning
        ? "#fff7ed"
        : "#fef2f2",

    color: high
      ? "#166534"
      : warning
        ? "#9a3412"
        : "#b91c1c",

    border: `1px solid ${
      high
        ? "#bbf7d0"
        : warning
          ? "#fed7aa"
          : "#fecaca"
    }`,
  };
}

const innerTabBarStyle = {
  display: "flex",
  gap: 5,
  padding: 4,
  width: "fit-content",
  border: "1px solid #dbe3ec",
  borderRadius: 8,
  background: "#f8fafc",
  flexWrap: "wrap",
};

const panelStyle = {
  border: "1px solid #dbe3ec",
  borderRadius: 10,
  background: "#fff",
  padding: 14,
  marginBottom: 14,
};

const filterGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit, minmax(180px, 1fr))",

  gap: 10,
};

const fieldStyle = {
  width: "100%",
  minHeight: 38,
  border: "1px solid #cbd5e1",
  borderRadius: 7,
  padding: "8px 10px",
  background: "#fff",
  color: "#1e293b",
  fontSize: "0.82rem",
};

const labelStyle = {
  display: "block",
  marginBottom: 5,
  color: "#475569",
  fontWeight: 700,
  fontSize: "0.75rem",
};

const pillStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "4px 8px",
  borderRadius: 20,
  background: "#f1f5f9",
  border: "1px solid #e2e8f0",
  color: "#475569",
  fontSize: "0.75rem",
};
const termFilterGridStyle = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit, minmax(165px, 1fr))",
  gap: 10,
  alignItems: "end",
};

const termDateButtonWrapperStyle = {
  display: "flex",
  alignItems: "end",
};

const courseRowStyle = {
  marginTop: 12,
  maxWidth: 650,
};

const courseSelectStyle = {
  ...fieldStyle,
  width: "100%",
};

const attendanceSummaryStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  marginTop: 12,
};

const actionBarStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
  padding: "10px 12px",
  borderBottom: "1px solid #e2e8f0",
  background: "#f8fafc",
};

const tableStyle = {
  width: "100%",
  minWidth: 2200,
  borderCollapse: "collapse",
  fontSize: "0.76rem",
};

const headerCellStyle = {
  padding: "9px 8px",
  borderRight: "1px solid #cbd5e1",
  borderBottom: "1px solid #cbd5e1",
  background: "#f8fafc",
  color: "#334155",
  textAlign: "left",
  whiteSpace: "nowrap",
  fontWeight: 700,
  position: "sticky",
  top: 0,
  zIndex: 2,
};

const bodyCellStyle = {
  padding: 8,
  borderRight: "1px solid #e2e8f0",
  borderBottom: "1px solid #e2e8f0",
  verticalAlign: "top",
  color: "#1e293b",
  background: "#fff",
};

const totalCellStyle = {
  ...bodyCellStyle,
  textAlign: "center",
  fontWeight: 700,
};

const weekCellStyle = {
  display: "grid",
  gap: 6,
};

const miniLabelStyle = {
  display: "grid",
  gap: 3,
  color: "#64748b",
  fontSize: "0.67rem",
  fontWeight: 600,
};

const checkboxRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  color: "#475569",
  fontSize: "0.69rem",
};

const cellInputStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 5,
  padding: "5px 6px",
  background: "#fff",
  fontSize: "0.74rem",
};

const emptyStyle = {
  padding: 35,
  textAlign: "center",
  color: "#64748b",
};

const secondaryButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#334155",
  borderRadius: 6,
  padding: "7px 10px",
  cursor: "pointer",
  fontSize: "0.79rem",
  fontWeight: 700,
};

const termDateButtonStyle = {
  ...secondaryButtonStyle,
  width: "100%",
  minHeight: 38,
  justifyContent: "center",
  border: "1px solid #056ec4",
  background: "#056ec4",
  color: "#fff",
};

const excelButtonStyle = {
  ...secondaryButtonStyle,
  border: "1px solid #15803d",
  background: "#15803d",
  color: "#fff",
};

const primaryButtonStyle = {
  ...secondaryButtonStyle,
  border: "1px solid #056ec4",
  background: "#056ec4",
  color: "#fff",
};
const topActionRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
  marginBottom: 14,
};